import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { generateCharacterSVG } from '../utils/characterSVG';
import CharacterGrid from './CharacterGrid';
import CustomizationOptions from './CustomizationOptions';
import ColorPicker from './ColorPicker';

const CharacterSection = ({ currentCharacter, currentColor, currentAccessory, characterColors, unlockedCharacters, onCharacterChange, onColorChange, onAccessoryChange }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();

  // 直接使用內聯樣式定義標題顏色
  const subTitleStyle = {
    fontWeight: 700,
    color: isDark ? '#0ea5e9' : '#6b21a8' // 深色模式使用 sky-500，淺色模式使用 purple-800
  };

  return (
    <div className="space-y-6">
      <div className="text-center max-w-xl mx-auto">
        <h2 className="text-3xl mb-2">
          <span style={subTitleStyle}>{t('chooseCharacter')}</span>
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 mx-auto">{t('unlockMore')}</p>
      </div>

      {/* 角色選擇網格 */}
      <CharacterGrid
        currentCharacter={currentCharacter}
        unlockedCharacters={unlockedCharacters}
        characterColors={characterColors}
        onCharacterChange={onCharacterChange}
      />

      {/* 預覽和自訂區域 - 並排顯示 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左側：即時預覽 */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
          <h3 className="text-xl font-semibold mb-6 text-center text-gray-700 dark:text-gray-200">
            👁️ {t('characterPreview')}
          </h3>
          <div className="space-y-6">
            {/* 角色展示 */}
            <div className="flex justify-center">
              <div className="w-60 h-60 flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-3xl p-4">
                <div className="w-full h-full animate-bounce" dangerouslySetInnerHTML={{ __html: generateCharacterSVG(currentCharacter, currentColor, currentAccessory) }} />
              </div>
            </div>

            {/* 角色信息 */}
            <div className="text-center">
              <h4 className="text-2xl font-bold mb-3 text-gray-800 dark:text-gray-100">
                {t(`characters.${currentCharacter}.name`)}
              </h4>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed text-center">
                {t(`characters.${currentCharacter}.description`)}
              </p>
            </div>

            {/* 特殊能力和喜好 */}
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-purple-100 dark:bg-purple-900/50 rounded-2xl p-4 flex flex-col items-center">
                <div className="text-lg mb-2">✨</div>
                <p className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-2 text-center">
                  {t('specialAbility')}
                </p>
                <p className="text-sm text-purple-600 dark:text-purple-300 leading-relaxed text-center">
                  {t(`characters.${currentCharacter}.ability`)}
                </p>
              </div>

              <div className="bg-blue-100 dark:bg-blue-900/50 rounded-2xl p-4 flex flex-col items-center">
                <div className="text-lg mb-2">🎵</div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2 text-center">
                  {t('favoriteType')}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-300 leading-relaxed text-center">
                  {t(`characters.${currentCharacter}.favoriteMusic`)}
                </p>
              </div>
            </div>

            {/* 當前自訂狀態 */}
            <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-4">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3 text-center">
                🎨 {t('currentCustomization') || '當前自訂'}
              </h5>
              <div className="flex items-center justify-center text-sm">
                <div className="flex items-center space-x-2 mr-4">
                  <div className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-500" style={{ backgroundColor: currentColor }} />
                  <span className="text-gray-600 dark:text-gray-300">{t('color') || '顏色'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t('accessory') || '配件'}:
                  </span>
                  <span className="text-gray-800 dark:text-gray-100 font-medium">
                    {currentAccessory === 'none' ? t('accessories.none') : t(`accessories.${currentAccessory}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 右側：自訂選項 */}
        <div className="space-y-6">
          {/* 配件選擇 */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
            <h3 className="text-xl font-semibold mb-6 text-center text-gray-700 dark:text-gray-200">
              ✨ {t('characterAccessories')}
            </h3>
            <CustomizationOptions
              currentAccessory={currentAccessory}
              onAccessoryChange={onAccessoryChange}
            />
          </div>

          {/* 顏色選擇 */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
            <h3 className="text-xl font-semibold mb-6 text-center text-gray-700 dark:text-gray-200">
              🎨 {t('customColors')}
            </h3>
            <ColorPicker
              currentColor={currentColor}
              onColorChange={onColorChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterSection;